import { useState, useEffect } from 'react'
import moment from 'moment'

import {
  formatDateTime,
  formatCurrency,
  formatPhoneNumber,
  capitalizeString,
  createWorkbook,
  addWorksheet,
  saveWorkbook,
  setColumnsAndRows,
  DEFAULTNUMHEADERS,
  setHeaders,
  numberFormat,
  currencyFormat,
} from '../../utilities/helpers'

import { getAgedReceivableReport } from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'

import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'

import { ReportActions } from '../ReportActions'
import { EmptyContainer } from '../EmptyContainer'
import { LoadingContainer } from '../LoadingContainer'

export default function AgedReceivablesWrapper({ organization }) {
  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)

  const [agedReceivables, setAgedReceivables] = useState(null)

  const [fromDate, setFromDate] = useState(null)

  const [toDate, setToDate] = useState(null)

  useEffect(() => {
    const fetchData = async () => {
      if (!fromDate || !toDate) return

      setIsLoadingData(true)

      try {
        const res = await getAgedReceivableReport(
          organization.uuid,
          fromDate?.toISOString(),
          toDate?.toISOString()
        )

        setAgedReceivables(res.data)
      } catch (error) {
        console.error('Error fetching aged receivables:', error)
      } finally {
        setIsLoadingData(false)
        completeRefresh()
      }
    }

    fetchData()
  }, [fromDate, toDate, organization.uuid, refreshToken])

  const handleExcelExport = async (
    organizationName,
    toDate,
    fromDate,
    reportData
  ) => {
    const dateRange = `${formatDateTime(moment(fromDate), 'dateOnly')} - ${formatDateTime(moment(toDate), 'dateOnly')}`

    const workbook = createWorkbook()
    const worksheet = addWorksheet('Aged Receivables', workbook)

    const format = (receivable) => {
      const {
        amountDue,
        created,
        type,
        name,
        organization,
        firstName,
        lastName,
        email,
        phoneNumber,
        invoiceId,
        invoiceLink,
        status,
        totalAmount,
      } = receivable

      return {
        type: capitalizeString(type),
        name,
        created,
        first_name: firstName ? capitalizeString(firstName) : 'N/A',
        last_name: lastName ? capitalizeString(lastName) : 'N/A',
        email: email ?? 'N/A',
        phone_number: formatPhoneNumber(phoneNumber),
        organization: organization ?? 'N/A',
        invoice_number: invoiceId,
        invoice_link: invoiceLink,
        total_amount: parseFloat(totalAmount),
        amount_due: parseFloat(amountDue),
        status: capitalizeString(status),
      }
    }

    const headers = [`Date Range: ${dateRange}`]

    setHeaders('Aged Receivables', organizationName, worksheet, headers)

    setColumnsAndRows(
      Object.keys(format(reportData)),
      Object.values(reportData),
      format,
      worksheet
    )

    const headerRows = DEFAULTNUMHEADERS + headers?.length

    currencyFormat(11, worksheet, headerRows)
    currencyFormat(12, worksheet, headerRows)

    // Generate and download Excel file
    // save excel worksheet
    saveWorkbook(workbook, 'aged receivables')
      .then()
      .catch((err) => alert(err.message))
  }

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates
    setFromDate(start)
    setToDate(end)
  }

  return (
    <>
      <section className="spacer-sm">
        <header className="section-header-sm">
          <div className="section-heading">
            <h1>Aged Receivables</h1>
          </div>
          <p className="section-header-desc">
            View all outstanding invoices generated from group sales, season
            packages, and flex packages.
          </p>
        </header>
        <ReportActions
          isLoading={isLoadingData}
          onRefresh={refresh}
          isRefreshing={isLoadingData}
          fromDate={fromDate}
          toDate={toDate}
          data={agedReceivables}
          onDateRangeChange={handleDateRangeChange}
          downloadReport={() =>
            handleExcelExport(
              organization?.name,
              toDate,
              fromDate,
              agedReceivables
            )
          }
        />
        <Results
          isLoading={isLoadingData}
          fromDate={fromDate}
          toDate={toDate}
          data={agedReceivables}
        />
      </section>
    </>
  )
}

const Receivable = ({ receivable }) => {
  return (
    <tr className="md-x md-x--scroll">
      <td>{capitalizeString(receivable?.type)}</td>
      <td className="text-left">{receivable?.name}</td>
      <td className="text-left">{receivable?.created ?? 'N/A'}</td>
      <td className="text-left">
        {receivable?.firstName
          ? capitalizeString(receivable?.firstName)
          : 'N/A'}
      </td>
      <td className="text-left">
        {receivable?.lastName ? capitalizeString(receivable?.lastName) : 'N/A'}
      </td>
      <td className="text-left">{receivable?.email ?? 'N/A'}</td>
      <td className="text-left">
        {formatPhoneNumber(receivable?.phoneNumber)}
      </td>
      <td className="text-left">{receivable?.organization ?? 'N/A'}</td>
      <td className="text-left">{receivable?.invoiceId}</td>
      <td className="text-left">{receivable?.invoiceLink}</td>
      <td className="text-left">{formatCurrency(receivable?.totalAmount)}</td>
      <td className="text-left">{formatCurrency(receivable?.amountDue)}</td>
      <td>{capitalizeString(receivable?.status)}</td>
    </tr>
  )
}

// Shared table headers
const TableHeader = () => (
  <thead>
    <tr className="md md--scroll">
      <th className="uppercase text-body">Type</th>
      <th className="text-left uppercase text-body">Name</th>
      <th className="text-left uppercase text-body">Created</th>
      <th className="text-left uppercase text-body">First name</th>
      <th className="text-left uppercase text-body">Last name</th>
      <th className="text-left uppercase text-body">Email</th>
      <th className="text-left uppercase text-body">Phone number</th>
      <th className="text-left uppercase text-body">Organization</th>
      <th className="text-left uppercase text-body">Invoice #</th>
      <th className="text-left uppercase text-body">Invoice link</th>
      <th className="text-left uppercase text-body">Total amount</th>
      <th className="text-left uppercase text-body">Amount due</th>
      <th className="text-left uppercase text-body">Status</th>
    </tr>
  </thead>
)

function AgedReceivablesTable({ agedReceivables }) {
  return (
    <div className="table-container">
      <div className="table-container--fixed-height--scrollable">
        <Table responsive>
          <TableHeader />
          <tbody>
            {agedReceivables?.map((receivable, index) => (
              <Receivable key={index} receivable={receivable} />
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  )
}

function Results({ isLoading, data, fromDate, toDate }) {
  if (!fromDate || !toDate) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>Get started by choosing your filters</p>
          <p className="subtitle subtitle--dark">
            Select an event, season or date range from the options above. Once
            you've made your selections, your report will appear right away.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  if (isLoading) {
    return <LoadingContainer />
  }

  if (!data || data?.length === 0) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>No data</p>
          <p className="subtitle subtitle--dark">
            There's currently no data for the specified date range. Please
            select a different date range.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <Card body className="card--md">
      <AgedReceivablesTable agedReceivables={data} />
    </Card>
  )
}
